import { cn } from '@/lib/utils'
import type { HTMLAttributes } from 'react'

// ─── Badge ────────────────────────────────────────────────────────────────────

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: 'default' | 'green' | 'accent' | 'yellow' | 'red'
}

export function Badge({ variant = 'default', className, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded px-1.5 py-0.5 text-[9px] font-bold tracking-widest uppercase',
        {
          'bg-border2 text-muted':           variant === 'default',
          'bg-green/10 text-green':           variant === 'green',
          'bg-accent/10 text-accent':         variant === 'accent',
          'bg-yellow/10 text-yellow':         variant === 'yellow',
          'bg-red/10 text-red':               variant === 'red',
        },
        className
      )}
      {...props}
    />
  )
}

// ─── Spinner ──────────────────────────────────────────────────────────────────

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cn('animate-spin h-4 w-4 text-accent', className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
    </svg>
  )
}

// ─── Label ────────────────────────────────────────────────────────────────────

export function Label({ className, ...props }: HTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={cn('block text-[10px] font-bold tracking-widest uppercase text-muted mb-1.5', className)}
      {...props}
    />
  )
}

// ─── Card ─────────────────────────────────────────────────────────────────────

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-lg border border-border bg-surface p-4', className)}
      {...props}
    />
  )
}

// ─── Empty State ──────────────────────────────────────────────────────────────

export function EmptyState({ message }: { message: string }) {
  return (
    <div className="flex h-40 items-center justify-center text-xs text-muted font-mono">
      {message}
    </div>
  )
}
